import React from 'react';
import { GenericFormPage } from '../../components/generic';
import { useCrmNote, CrmNote } from './useData';
import {
  moduleConfig,
  getInitialFormData,
  mapItemToFormData,
} from './config';

const Form: React.FC = () => {
  const configWithOptions = (() => {
    const fields = moduleConfig.fields.map((field) => {
      if (field.name === 'clientId') {
        return {
          ...field,
          type: 'select' as const,
          label: 'Client',
          placeholder: 'Select a client (optional)',
          options: [{ value: '', label: 'None' }],
          apiEndpoint: '/crm-clients',
          labelField: 'clientName',
          valueField: 'clientId' as const,
        };
      }
      if (field.name === 'noteFrom') {
        return {
          ...field,
          type: 'select' as const,
          label: 'From',
          placeholder: 'Select a user',
          options: [],
          apiEndpoint: '/users',
          labelField: 'userDisplayName',
          valueField: 'id' as const,
        };
      }
      if (field.name === 'noteTo') {
        return {
          ...field,
          type: 'select' as const,
          label: 'To',
          placeholder: 'Select a user',
          options: [],
          apiEndpoint: '/users',
          labelField: 'userDisplayName',
          valueField: 'id' as const,
        };
      }
      return field;
    });

    // Form data allows foreign keys as string for select components
    type FormCrmNote = Omit<CrmNote, 'clientId' | 'noteFrom' | 'noteTo'> & {
      clientId?: string | number;
      noteFrom?: string | number;
      noteTo?: string | number;
    };

    // Custom mapItemToFormData that converts foreign keys to string for select
    const customMapItemToFormData = (item: CrmNote): Partial<FormCrmNote> => {
      const baseData = mapItemToFormData(item);
      return {
        ...baseData,
        clientId: baseData.clientId ? String(baseData.clientId) : '',
        noteFrom: baseData.noteFrom ? String(baseData.noteFrom) : '',
        noteTo: baseData.noteTo ? String(baseData.noteTo) : '',
      };
    };

    // Custom getInitialFormData
    const customGetInitialFormData = (): Partial<FormCrmNote> => {
      return {
        ...getInitialFormData(),
        clientId: '',
        noteFrom: '',
        noteTo: '',
      };
    };

    // Custom create/update handlers that convert foreign key strings back to numbers
    const customCreate = async (data: Partial<FormCrmNote>) => {
      const processedData: Partial<CrmNote> = {
        ...data,
        clientId: (data.clientId !== undefined && data.clientId !== null && String(data.clientId) !== '') 
          ? Number(data.clientId) 
          : undefined,
        noteFrom: (data.noteFrom !== undefined && data.noteFrom !== null && String(data.noteFrom) !== '') 
          ? Number(data.noteFrom) 
          : undefined,
        noteTo: (data.noteTo !== undefined && data.noteTo !== null && String(data.noteTo) !== '') 
          ? Number(data.noteTo) 
          : undefined,
      };
      return await moduleConfig.form.create(processedData);
    };

    const customUpdate = async (id: number, data: Partial<FormCrmNote>) => {
      const processedData: Partial<CrmNote> = {
        ...data,
        clientId: (data.clientId !== undefined && data.clientId !== null && String(data.clientId) !== '') 
          ? Number(data.clientId) 
          : undefined,
        noteFrom: (data.noteFrom !== undefined && data.noteFrom !== null && String(data.noteFrom) !== '') 
          ? Number(data.noteFrom) 
          : undefined,
        noteTo: (data.noteTo !== undefined && data.noteTo !== null && String(data.noteTo) !== '') 
          ? Number(data.noteTo) 
          : undefined,
      };
      return await moduleConfig.form.update(id, processedData);
    };

    return {
      ...moduleConfig,
      fields,
      getInitialFormData: customGetInitialFormData as () => Partial<CrmNote>,
      mapItemToFormData: customMapItemToFormData as (item: CrmNote) => Partial<CrmNote>,
      form: {
        ...moduleConfig.form,
        create: customCreate as (data: Partial<CrmNote>) => Promise<CrmNote>,
        update: customUpdate as (id: number, data: Partial<CrmNote>) => Promise<CrmNote>,
      },
    };
  })();

  return (
    <GenericFormPage<CrmNote>
      useData={useCrmNote}
      config={configWithOptions}
    />
  );
};

export default Form;

